import { FC, useState } from 'react';
import { useGetRouteDetailsByIdQuery } from '../../../../generated/graphql';
import { useObservationDateQueryParam } from '../../../../hooks';
import { Priority } from '../../../../types/enums';
import { filterHighestPriorityCurrentStops } from '../../../../utils';
import { stopBelongsToJourneyPattern } from '../../Common/utils';
import { getEligibleStopsAlongRoute } from '../../Routes/EditRoute/useEditRouteJourneyPattern';
import { RouteRow } from './RouteRow';
import { RouteRowLoader } from './RouteRowLoader';
import { RouteStopListItem } from './RouteStopListItem';

type LineRouteListItemProps = {
  readonly routeId: UUID;
  readonly hasContinuingVersion: boolean;
  readonly showUnusedStops: boolean;
  readonly isLast: boolean;
};

const testIds = {
  lineRouteListItem: 'LineRouteListItem',
};
export const LineRouteListItem: FC<LineRouteListItemProps> = ({
  routeId,
  hasContinuingVersion,
  showUnusedStops,
  isLast,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const onToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const { observationDate } = useObservationDateQueryParam();

  const routeResult = useGetRouteDetailsByIdQuery({
    variables: { routeId },
  });
  const route = routeResult.data?.route_route_by_pk;

  if (!route) {
    // Display a loader until all details have been loaded.
    // Note: we would actually have some information about the route that we could display already,
    // but made a design decision not to show anything until whole row can be shown.
    return (
      <li>
        <RouteRowLoader />
      </li>
    );
  }

  /**
   * Stops to display. Show the highest priority version of stops' valid instances.
   * If "show unused stops" is selected, also show stops that are not in the journey pattern
   * but are along route geometry.
   */
  const stopsAlongRoute = getEligibleStopsAlongRoute(route);
  const highestPriorityCurrentStops = filterHighestPriorityCurrentStops(
    stopsAlongRoute,
    observationDate,
    route.priority === Priority.Draft,
  );
  const displayedStops = showUnusedStops
    ? highestPriorityCurrentStops // Return stops array unchanged
    : highestPriorityCurrentStops.filter((stop) =>
        stopBelongsToJourneyPattern(stop, route.route_id),
      );

  const directionAndLabelElementId = `direction-and-label-${route.label}-${route.variant}-${route.direction}`;
  const routeStopListElementId = 'route-stop-list';

  return (
    <li data-testid={testIds.lineRouteListItem}>
      <RouteRow
        directionAndLabelId={directionAndLabelElementId}
        key={routeId}
        route={route}
        hasContinuingVersion={hasContinuingVersion}
        observationDate={observationDate}
        isExpanded={isExpanded}
        onToggle={onToggle}
        isLast={isLast}
        controls={routeStopListElementId}
      />
      {isExpanded && (
        <div className="@container overflow-x-auto" id={routeStopListElementId}>
          <table
            className="w-full border-collapse"
            aria-labelledby={directionAndLabelElementId}
          >
            <tbody>
              {displayedStops.map((item) => (
                <RouteStopListItem
                  key={item.scheduled_stop_point_id}
                  stop={item}
                  route={route}
                  observationDate={observationDate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </li>
  );
};
